import React, {useMemo, useState} from 'react';
import {Image, StyleSheet, Text, View} from 'react-native';

/**
 * Pleasant, evenly-spread hues used for the initials fallback. Each user is
 * mapped deterministically to one of these via a name hash, so the same person
 * always gets the same colour across the app.
 * @type {number[]}
 */
const AVATAR_HUES = [212, 262, 292, 338, 12, 28, 152, 174, 198];

/**
 * Resolve a usable photo/avatar URL from the many shapes a user object can take
 * across the backend (self user, mention search, management list, note author…).
 *
 * @param {string} [photo] explicit override URL
 * @param {object} [user] user-like object
 * @returns {string} a URL, or '' when only initials should be shown
 */
const resolvePhotoUrl = (photo, user) => {
    const url = photo
        || user?.photo
        || user?.avatarUrl
        || user?.avatar_url
        || user?.avatar
        || user?.image;
    return typeof url === 'string' ? url.trim() : '';
};

/**
 * Best-effort human name from a user-like object (mirrors the initials logic
 * already used in ManagementUsersScreen / MentionInput).
 *
 * @param {object|string} [user]
 * @returns {string}
 */
const resolveName = (user) => {
    if (!user) return '';
    if (typeof user === 'string') return user.trim();
    if (user.name && typeof user.name === 'object') {
        return `${user.name.first || ''} ${user.name.last || ''}`.trim();
    }
    if (typeof user.name === 'string' && user.name.trim()) return user.name.trim();
    const composed = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    if (composed) return composed;
    return (user.username || user.userName || user.email || '').trim();
};

/**
 * Derive up to two uppercase initials from a display name.
 *
 * @param {string} name
 * @returns {string} 1-2 letters, or '?' when nothing is derivable
 */
const initialsOf = (name) => {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
};

/**
 * Map an arbitrary string to a stable HSL colour pair (background + readable
 * text). Uses a simple deterministic hash so the colour never flickers.
 *
 * @param {string} seed
 * @returns {{bg: string, fg: string}}
 */
const colorFromSeed = (seed) => {
    const str = String(seed || 'happy');
    let hash = 0;
    for (let i = 0; i < str.length; i += 1) {
        hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
    }
    const hue = AVATAR_HUES[hash % AVATAR_HUES.length];
    return {bg: `hsl(${hue}, 62%, 90%)`, fg: `hsl(${hue}, 55%, 34%)`};
};

/**
 * Shared, reusable circular avatar.
 *
 * Resolves a photo URL from `photo` or any of the common fields on `user`
 * (`photo` / `avatarUrl` / `avatar_url` / `avatar` / `image`). When a URL is
 * available it renders a circular {@link Image}; otherwise it falls back to a
 * deterministically-coloured circle showing the user's initials. The component
 * never fabricates data — pass whatever user object you have and the initials
 * fallback handles the rest.
 *
 * @param {object} props
 * @param {object|string} [props.user] user-like object (or a plain name string)
 * @param {string} [props.photo] explicit photo URL override (wins over `user`)
 * @param {number} [props.size=40] diameter in px (border radius is size/2)
 * @param {import('react-native').StyleProp<import('react-native').ViewStyle>} [props.style] extra container style
 * @returns {React.ReactElement}
 */
export default function Avatar({user, photo, size = 40, style}) {
    const [failed, setFailed] = useState(false);

    const url = useMemo(() => resolvePhotoUrl(photo, user), [photo, user]);
    const name = useMemo(() => resolveName(user), [user]);
    const initials = useMemo(() => initialsOf(name), [name]);
    const {bg, fg} = useMemo(() => colorFromSeed(name || url || 'happy'), [name, url]);

    const dimension = {width: size, height: size, borderRadius: size / 2};

    if (url && !failed) {
        return (
            <Image
                source={{uri: url}}
                onError={() => setFailed(true)}
                style={[styles.image, dimension, style]}
            />
        );
    }

    return (
        <View style={[styles.fallback, dimension, {backgroundColor: bg}, style]}>
            <Text
                allowFontScaling={false}
                style={[styles.initials, {color: fg, fontSize: Math.max(11, size * 0.4)}]}
            >
                {initials}
            </Text>
        </View>
    );
}

const styles = StyleSheet.create({
    image: {backgroundColor: '#eef0f4'},
    fallback: {alignItems: 'center', justifyContent: 'center'},
    initials: {fontWeight: '800', letterSpacing: 0.3},
});
